import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PaginatedSortableTable from "../components/PaginatedSortableTable";
import DateTimeRangePicker from "../components/DateTimeRangePicker";
import {
  getInitialDateTimeRange,
  getPresetDateTimeRange,
} from "../utils/dateRange";
dayjs.extend(utc);
dayjs.extend(timezone);

export default function LogsSend() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const { setIsLoggedIn } = useOutletContext();
  const navigate = useNavigate();
  const initialRange = getInitialDateTimeRange();
  const [startDateTime, setStartDateTime] = useState(initialRange.start);
  const [endDateTime, setEndDateTime] = useState(initialRange.end);
  const [users, setUsers] = useState(null);
  const [logsData, setLogsData] = useState(null);
  const [totalLogs, setToTalLogs] = useState(null);

  const [searchField, setSearchField] = useState("username"); // username | siteUsername | agentId
  const [searchKeyword, setSearchKeyword] = useState("");
  const [actionFilter, setActionFilter] = useState(""); // bet | balance | cancel | …

  const sortOptions = [
    { value: "time_request:asc", label: "요청시간 - 내림차순" },
    { value: "time_request:desc", label: "요청시간 - 오름차순" },
  ];

  const handleDateRange = (type) => {
    const range = getPresetDateTimeRange(type);
    setStartDateTime(range.start);
    setEndDateTime(range.end);
  };

  useEffect(() => {
    if (!logsData) return;
    const filtered = logsData.filter((log) => {
      // 3-1) 액션 필터
      if (actionFilter && log.method !== actionFilter) return false;

      // 3-2) 검색 필드/키워드 필터
      if (searchKeyword) {
        const value = (() => {
          switch (searchField) {
            case "username":
              return log.userId;
            case "siteUsername": // siteUsername 은 user 트리에서 찾아야 함
              const u = getAllUsers().find((u) => u.userId === log.userId);
              return u?.siteId || "";
            case "agentId": // agentId 는 log.agentId 로 가정
              return log.userId.substring(0, 5);
            default:
              return "";
          }
        })();
        if (!value.includes(searchKeyword)) return false;
      }

      return true;
    });
    setToTalLogs(filtered);
  }, [logsData, searchField, searchKeyword, actionFilter]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE}/api/Users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUsers(res.data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          if (
            err.response.status === 401 ||
            err.response.status === 403 ||
            err.response.status === 500
          ) {
            localStorage.removeItem("token");
            setIsLoggedIn(false); // 로그인 상태 false 처리
            navigate("/login"); // 로그인 화면으로 리다이렉트
            return;
          }
        }
        console.error("유저 정보 불러오기 실패:", err);
      }
    };

    fetchUserInfo();
  }, []);

  const fetchLogsData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/LogsSend`, {
        params: {
          processed_at_start: startDateTime.replace("T", " ") + ":00",
          processed_at_end: endDateTime.replace("T", " ") + ":59",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLogsData(res.data);
      setToTalLogs(res.data);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (
          err.response.status === 401 ||
          err.response.status === 403 ||
          err.response.status === 500
        ) {
          localStorage.removeItem("token");
          setIsLoggedIn(false); // 로그인 상태 false 처리
          navigate("/login"); // 로그인 화면으로 리다이렉트
          return;
        }
      }
      console.error("조회 실패:", err);
    }
  };

  const getAllUsers = () => {
    const list = [];
    if (!users) return list;

    // 1) 최상위 users.users 에 속한 유저들
    users.users.forEach((u) => list.push(u));

    // 2) 에이전트 트리를 타고 내려가며 모든 users 배열 합치기
    const recurseAgents = (agentNode) => {
      // 해당 에이전트의 직속 users
      if (agentNode.users) {
        agentNode.users.forEach((u) => list.push(u));
      }
      // 그 에이전트의 하위 에이전트들
      if (agentNode.agents) {
        agentNode.agents.forEach((childAgent) => recurseAgents(childAgent));
      }
    };

    // 최상위 agents 배열부터 시작
    users.agents.forEach((agent) => recurseAgents(agent));
    return list;
  };
  const allUsers = getAllUsers();

  const fmtDate = (ms) => dayjs(ms).tz("Asia/Seoul").format("YYYY-MM-DD");
  const fmtTime = (ms) =>
    dayjs(ms).tz("Asia/Seoul").format("HH:mm:ss:SSS") + " UTC +9";

  useEffect(() => {
    if (!users) return;
    fetchLogsData();
  }, [users]);

  const renderRow = (log, idx) => {
    if (!totalLogs || totalLogs.length === 0) {
      return (
        <tr>
          <td className="text-center span-cell-emt" colSpan="14">
            <div className="nk-order-ovwg-ck text-red">
              <h5>조회된 정보가 없습니다.</h5>
            </div>
          </td>
        </tr>
      );
    }

    const user = getAllUsers().find((u) => u.userId === log.userId) || {};
    const agentId = user.agent || "-";

    return (
      <tr key={log.idx} data-pos-y="0">

      

        {/* siteId 부분 */}
        <td
          className=""
          colSpan="1"
          data-columns="gameList-col-3"
          data-org-colspan="1"
          data-pos-x="3"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          <div className="user-toggle">
              <div className="user-toggle">
                <div className="user-info">
                  <span className="badge rounded-pill badge-dim bg-outline-info me-1">
                    지갑
                  </span>
                  <em className="badge rounded-pill badge-dim bg-outline-info me-1 simm">
                    S
                  </em>
                  <div className="user-name">
                    <span className="badge rounded-pill badge-dim bg-outline-danger me-1">
                      아이디
                    </span>{" "}
                    {log.userId || "-"}
                  </div>
                  <div className="user-status">
                    <span className="badge rounded-pill badge-dim bg-outline-primary me-1">
                      사이트아이디
                    </span>
                    {log.userId || "-"}
                  </div>
                </div>
              </div>

          </div>
        </td>

        {/* 화폐 */}
        <td
          className=""
          colSpan="1"
          data-columns="gameList-col-4"
          data-org-colspan="1"
          data-pos-x="4"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          KRW
        </td>

        {/* 액션(method) */}
        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-5"
          data-org-colspan="1"
          data-pos-x="5"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
            <span className="badge me-1 bg-primary">
            {log.method === "bet"
              ? "베팅"
              : log.method === "result"
              ? "당첨"
              : log.method === "balance"
              ? "잔액조회"
              : log.method === "refund"
              ? "취소"
              : log.method}
          </span>
        </td>

        {/* 결과 */}
        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-6"
          data-org-colspan="1"
          data-pos-x="6"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          <span className={"badge me-1 bg-danger"}>실패</span>
        </td>

        {/* 오류코드 */}
        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-7"
          data-org-colspan="1"
          data-pos-x="7"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          {log.time_response == null ? 102 : log.e_code} 
          
        </td>

        {/* 메시지 */}
        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-8"
          data-org-colspan="1"
          data-pos-x="8"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          {log.response}
        </td>

        {/* 요청시간 */}
        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-9"
          data-org-colspan="1"
          data-pos-x="9"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          <span style={{ fontSize: "95%" }}>
            {fmtDate(log.time_request)}
            <font className="dateInHour"> ({fmtTime(log.time_request)})</font>
          </span>
        </td>
        {/* 응답시간 */}
        
      </tr>
    );
  };

  return (
    <div className="nk-content">
      <div className="container-fluid">
        <div className="nk-content-inner">
          <div className="nk-content-body">
            <div className="nk-block d-flex vLogsAgent">
              <div className="w-100 card card-bordered">
                <div className="card-inner-group">
                  <div className="card-inner p-3 p-0">
                    <form
                      className="mb-0"
                      onSubmit={(e) => {
                        e.preventDefault();
                        fetchLogsData();
                      }}
                    >
                      <DateTimeRangePicker
                        startDateTime={startDateTime}
                        endDateTime={endDateTime}
                        onStartChange={setStartDateTime}
                        onEndChange={setEndDateTime}
                        onPresetChange={handleDateRange}
                      >
                        <div className="col-12 col-sm-6 col-lg-6 col-xl-4 col-xxl-5">
                          <div className="form-control-wrap">
                            <div
                              className="input-group"
                              style={{
                                flexWrap: "nowrap",
                              }}
                            >
                              <div className="input-group-prepend">
                                <button
                                  type="button"
                                  className="btn btn-outline-light btn-dim dropdown-toggle"
                                  data-bs-toggle="dropdown"
                                >
                                  <span className="dropdown-menu-text">
                                    {searchField === "username"
                                      ? "아이디"
                                      : searchField === "siteUsername"
                                        ? "사이트 아이디"
                                        : "에이전트 아이디"}
                                  </span>
                                  <em className="icon mx-n1 ni ni-chevron-down" />
                                </button>
                                <div className="dropdown-menu">
                                  {["username", "siteUsername", "agentId"].map(
                                    (f) => (
                                      <a
                                        key={f}
                                        href="#"
                                        className="dropdown-item"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setSearchField(f);
                                        }}
                                      >
                                        {f === "username"
                                          ? "아이디"
                                          : f === "siteUsername"
                                            ? "사이트 아이디"
                                            : "에이전트 아이디"}
                                      </a>
                                    ),
                                  )}
                                </div>
                              </div>

                              <input
                                type="text"
                                className="form-control"
                                placeholder="검색어"
                                value={searchKeyword}
                                onChange={(e) =>
                                  setSearchKeyword(e.target.value)
                                }
                              />

                              <select
                                className="form-select"
                                value={actionFilter}
                                onChange={(e) =>
                                  setActionFilter(e.target.value)
                                }
                              >
                                <option value="">==액션 전체==</option>
                                <option value="balance">잔액조회</option>
                                <option value="bet">베팅</option>
                                <option value="result">결과</option>
                                <option value="cancel">취소</option>
                              </select>

                              <div className="input-group-append">
                                <button
                                  type="submit"
                                  className="btn btn-outline-primary btn-dim"
                                >
                                  <em className="icon ni ni-search me-2" />
                                  검색
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </DateTimeRangePicker>
                    </form>
                  </div>

                  <div className="card-inner tableBox px-0 pb-0">
                    <div className="table-wrapper">
                      <div className="table-responsive">
                        <div data-pattern="priority-columns">
                          <table
                            cellSpacing="0"
                            className="table table-small-font table-tighten table-bordered table-striped"
                          >
                            <thead>
                              <tr data-pos-y="0">
                                
                                <th
                                  className="text-center"
                                  data-pos-x="3"
                                  id="gameList-col-3"
                                >
                                  회원정보
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="4"
                                  id="gameList-col-4"
                                >
                                  화폐
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="5"
                                  id="gameList-col-5"
                                >
                                  액션
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="6"
                                  id="gameList-col-6"
                                >
                                  결과
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="7"
                                  id="gameList-col-7"
                                >
                                  오류코드
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="8"
                                  id="gameList-col-8"
                                >
                                  메세지
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="9"
                                  id="gameList-col-9"
                                >
                                  요청시간
                                </th>
                              </tr>
                            </thead>

                            {totalLogs && (
                              <PaginatedSortableTable
                                data={totalLogs}
                                renderRow={renderRow}
                                defaultSortField="time_request"
                                defaultSortOrder="desc"
                                sortOptions={sortOptions}
                              />
                            )}
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className="card-inner">
                    <div className="row justify-end">
                      <div className="col-12 col-md-4 col-xxl-4">
                        <ul
                          className="pagination justify-content-center justify-content-md-start"
                          style={{
                            float: "right",
                          }}
                        >
                          <li className="page-item">
                            <a className="page-link disabled" href="#">
                              <i className="fas fa-angle-double-left" />
                              <font>처음</font>
                            </a>
                          </li>
                          <li className="page-item">
                            <a className="page-link disabled" href="#">
                              <i className="fa fa-angle-left" />
                              <font>이전</font>
                            </a>
                          </li>
                          <li className="page-item">
                            <input
                              className="form-control text-center"
                              type="number"
                            />
                          </li>
                          <li className="page-item">
                            <a className="page-link" href="#">
                              <font>다음</font>
                              <i className="fa fa-angle-right" />
                            </a>
                          </li>
                        </ul>
                      </div>
                      <div className="col-6 col-md-2 col-xxl-2">
                        <select className="form-select">
                          <option value="10">리스트 - 10</option>
                          <option value="20">리스트 - 20</option>
                          <option value="50">리스트 - 50</option>
                          <option value="100">리스트 - 100</option>
                        </select>
                      </div>
                      <div className="col-6 col-md-2 col-xxl-2">
                        <select className="form-select">
                          <option value="-1">처리시간 - 내림차순</option>
                          <option value="1">처리시간 - 오름차순</option>
                        </select>
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
